import React from 'react';

import CardDialog from '~/components/CardDialog';
import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import Text from '~/components/core/TextComponents/Text';
import type { ClaimPolymorph } from '~/components/types/claim-types';
import useDataFetcher from '~/components/useDataFetcher';
import type { Exposure } from '~/server_shared/swagger/data-contracts';

import IncomeProtectionSettingsForm from './IncomeProtectionSettingsForm/IncomeProtectionSettingsForm';
import { getIncomeProtectionDaFullDetailsUrl } from './apiService';
import IncomeProtectionDAScreenContent from './IncomeProtectionDAScreenContent';
import TEXTS from './texts';
import type { IncomeProtectionDaFullDetailsResponse } from './types';

const IncomeProtectionScreenOrCreationDialog = ({
  open,
  onClose,
  exposure,
  claim,
  viewOnly,
}: {
  open: boolean;
  onClose: () => void;
  exposure: Exposure & { id: number };
  claim: ClaimPolymorph;
  viewOnly: boolean;
}) => {
  const { data, isLoading, isError, reloadData } = useDataFetcher<IncomeProtectionDaFullDetailsResponse>(
    getIncomeProtectionDaFullDetailsUrl({
      organizationId: claim.organization_id,
      claimId: claim.id,
      exposureId: exposure.id,
    })
  );

  const isInitialSetup = !(isLoading || isError || data?.setting);
  const isPaymentsScreen = Boolean(data?.setting);

  const dialogProps = {
    isDialog: true,
    open,
    onClose,
    trackAlt: 'Income Protection Settings Creation or Payments Screen',
    ...(isPaymentsScreen
      ? { fullScreen: true, hideTitleContainer: true, containerClassName: 'bg-slate-100 grid' }
      : { ...IncomeProtectionSettingsForm.RECOMMENDED_CARD_DIALOG_VIEW_PROPS, title: null }),
    ...(isInitialSetup ? { title: IncomeProtectionSettingsForm.RECOMMENDED_CARD_DIALOG_VIEW_PROPS.title } : {}),
  };

  return (
    <CardDialog {...dialogProps}>
      {isLoading || isError ? (
        <SkeletonTable rowsCount={5} columnsCount={2} isError={isError} />
      ) : data?.setting ? (
        <IncomeProtectionDAScreenContent
          setting={data.setting}
          summary={data.summary}
          exposure={exposure}
          claim={claim}
          onClose={onClose}
          onSettingUpdated={reloadData}
          viewOnly={viewOnly}
          isPaymentNotificationEnabled={data.is_payment_notification_enabled}
          isMedicalDocumentNotificationEnabled={data.is_medical_document_notification_enabled}
        />
      ) : viewOnly ? (
        <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR}>
          {TEXTS.noSettingsAndViewOnly}
        </Text>
      ) : (
        <IncomeProtectionSettingsForm onCancel={onClose} onSuccess={reloadData} exposure={exposure} claim={claim} />
      )}
    </CardDialog>
  );
};

export default IncomeProtectionScreenOrCreationDialog;
